/**
 * Web shim for the Electron preload bridge (`window.api`).
 *
 * The EXISTING SpecForma renderer is plain React that talks to the EC2 API over
 * HTTP for all data; it only reaches the desktop through `window.api`. When the
 * app runs in a real browser (no Electron preload), this module installs a
 * browser-native implementation of that bridge so the SAME app runs unchanged:
 *   - token / settings / api-key  -> localStorage
 *   - file pickers                -> <input type=file> / browser download
 *   - menu / updater / zoom / app-close -> web-appropriate no-ops
 *   - Word engine / PDF / harvester / drive-scan -> desktop-only (clear error)
 *
 * It self-installs on import and ONLY when `window.api` is absent, so importing
 * it in Electron is a harmless no-op (the real preload wins).
 */
import type { SpecFormaApi } from '../../shared/ipc-api';

const DESKTOP_ONLY = (feature: string) =>
  new Error(`${feature} runs in the SpecForma desktop app. Open the desktop app to use it.`);

const lsGet = (k: string): string | null => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};
const lsSet = (k: string, v: string) => {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* storage disabled — ignore */
  }
};
const lsDel = (k: string) => {
  try {
    window.localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
};

/** Open a browser file picker and resolve the chosen file as a base64 data URL. */
function pickFile(
  accept: string
): Promise<{ data: string; name: string; mimeType: string } | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () =>
        resolve({ data: String(reader.result), name: file.name, mimeType: file.type });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    // If the user cancels, no change fires; that's an unresolved promise which is
    // acceptable for a one-shot dialog (the caller simply gets no file).
    input.click();
  });
}

/**
 * CSV import (Admin bulk import) — the browser has no file paths, so
 * `openCsvDialog` picks a file, decodes it EXACTLY like main.ts `csv:parse`
 * (UTF-8 BOM strip; replacement-char fallback to Windows-1252 for Excel/Ajera
 * exports), caches the text under a pseudo-path, and `parseCsvFile` parses
 * the cached text with the same semantics as the desktop handler.
 */
const webCsvCache = new Map<string, string>();

function pickCsvText(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => {
        const buf = new Uint8Array(reader.result as ArrayBuffer);
        let raw: string;
        if (buf[0] === 0xef && buf[1] === 0xbb && buf[2] === 0xbf) {
          // UTF-8 BOM
          raw = new TextDecoder('utf-8').decode(buf.subarray(3));
        } else {
          // Try UTF-8; if replacement chars appear, fall back to Windows-1252
          const utf8 = new TextDecoder('utf-8').decode(buf);
          raw = utf8.includes('�') ? new TextDecoder('windows-1252').decode(buf) : utf8;
        }
        const pseudoPath = `web-csv:${file.name}`;
        webCsvCache.set(pseudoPath, raw);
        resolve(pseudoPath);
      };
      reader.onerror = () => resolve(null);
      reader.readAsArrayBuffer(file);
    };
    // Cancel fires no change event — unresolved promise, same one-shot
    // trade-off as pickFile above.
    input.click();
  });
}

/** Line parser identical to main.ts `csv:parse` (quote toggle, comma split, trim). */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  result.push(current.trim());
  return result;
}

const webApi: SpecFormaApi = {
  // ── Current user: web has no OS login; the real user comes from authService.
  getCurrentUser: async () => ({ name: '', username: '', email: '' }) as never,

  // ── Settings / API key (localStorage)
  loadSettings: async () => {
    const raw = lsGet('sf.settings');
    return (raw ? JSON.parse(raw) : {}) as never;
  },
  saveSettings: async (s) => {
    lsSet('sf.settings', JSON.stringify(s));
  },
  loadApiKey: async () => {
    const key = lsGet('sf.apiKey') ?? '';
    return { hasKey: !!key, masked: key ? `••••${key.slice(-4)}` : '' };
  },
  saveApiKey: async (key: string) => {
    lsSet('sf.apiKey', key);
    return true;
  },

  // ── Auth token (localStorage) — the one bridge call login/session depends on.
  loadAuthToken: async () => lsGet('sf.authToken') ?? '',
  saveAuthToken: async (token: string) => {
    lsSet('sf.authToken', token);
    return true;
  },
  clearAuthToken: async () => {
    lsDel('sf.authToken');
    return true;
  },

  // ── Browser sign-in handoff — desktop-only concepts; web equivalents.
  openExternal: async (url: string) => {
    if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
  },
  onDeepLink: (_callback: (url: string) => void) => () => {},

  // ── Dev mode
  isDevMode: async () => false,

  // ── Multi-window — desktop-only; the browser has its own tabs.
  windowOpen: async (_address: string) => false,
  isPrimary: async () => true,

  // ── File dialogs → browser pickers / downloads
  openImageDialog: async () => {
    const f = await pickFile('image/*');
    if (!f) return null;
    // Resolve natural dimensions for the editor.
    const dims = await new Promise<{ width: number; height: number }>((res) => {
      const img = new Image();
      img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => res({ width: 0, height: 0 });
      img.src = f.data;
    });
    return { data: f.data, name: f.name, mimeType: f.mimeType, ...dims };
  },
  openExpenseFileDialog: async () => {
    const f = await pickFile('image/*,application/pdf');
    return f ? { data: f.data, fileName: f.name, mimeType: f.mimeType } : null;
  },
  openDocxDialog: async () => [], // engine import is desktop-only; no path in web
  saveDocxDialog: async () => null,
  saveDotxDialog: async () => null,
  openDotxDialog: async () => null,
  selectFolderDialog: async () => null,
  openCsvDialog: () => pickCsvText(),
  showOpenDialog: async () => ({ canceled: true, filePaths: [] }),

  // ── HAR Sync — desktop only (reads a local/OneDrive workbook)
  openHarWorkbookDialog: async () => null,
  readHarWorkbook: async () => {
    throw DESKTOP_ONLY('HAR sync');
  },
  loadHarPrefs: async () => {
    try {
      return JSON.parse(lsGet('sf.harSync.prefs') || '{}');
    } catch {
      return {};
    }
  },
  saveHarPrefs: async (prefs: { source?: string; autoApply?: boolean }) => {
    lsSet('sf.harSync.prefs', JSON.stringify(prefs || {}));
    return true;
  },
  // P drive check reads a local/network filesystem path — desktop-only.
  // The tab itself stays gated by canHarSync (RWB account + Electron), so
  // this branch is defensive, never reached in normal web use.
  listHarProjectFolders: async () => ({
    ok: false as const,
    reason: 'unreachable' as const,
    message: 'The P drive check runs in the SpecForma desktop app.',
  }),

  // ── Word engine / PDF / harvester / drive — desktop only (clear, non-crashing)
  createTemplate: async () => {
    throw DESKTOP_ONLY('Creating a Word template');
  },
  importTemplate: async () => {
    throw DESKTOP_ONLY('Importing a Word template');
  },
  formatDocuments: async () => {
    throw DESKTOP_ONLY('Formatting documents');
  },
  scanFormatterDocuments: async () => {
    throw DESKTOP_ONLY('Scanning documents');
  },
  convertToPdf: async () => {
    throw DESKTOP_ONLY('PDF conversion');
  },
  checkPdfConflicts: async () => ({ folderMissing: false, conflicts: [] }),
  exportProjectSpecs: async () => {
    throw DESKTOP_ONLY('Exporting project specs');
  },
  scanProjectDrive: async () => [],
  parseDocx: async () => {
    throw DESKTOP_ONLY('Parsing .docx');
  },
  extractDocx: async () => {
    throw DESKTOP_ONLY('Extracting .docx');
  },
  classifyDocx: async () => {
    throw DESKTOP_ONLY('AI classification');
  },
  // Parses the text cached by openCsvDialog — output shape and parsing
  // semantics identical to main.ts `csv:parse` (headers from first line,
  // rows keyed by header, missing cells default to '').
  parseCsvFile: async (filePath: string) => {
    const raw = webCsvCache.get(filePath);
    if (raw == null) return { headers: [], rows: [] };
    const lines = raw.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return { headers: [], rows: [] };
    const headers = parseCsvLine(lines[0]);
    const rows = lines.slice(1).map((line) => {
      const vals = parseCsvLine(line);
      const row: Record<string, string> = {};
      headers.forEach((h, i) => {
        row[h] = vals[i] || '';
      });
      return row;
    });
    return { headers, rows };
  },

  // ── File system — desktop only
  readFile: async () => {
    throw DESKTOP_ONLY('Reading a local file');
  },
  writeFile: async () => {
    throw DESKTOP_ONLY('Writing a local file');
  },
  checkFilesExist: async () => [],
  renameSpecFiles: async () => [],
  archiveOriginals: async () => {
    throw DESKTOP_ONLY('Archiving originals');
  },
  writeFormatReport: async () => {
    throw DESKTOP_ONLY('Writing a formatting report');
  },
  applyDirtyFixes: async () => {
    throw DESKTOP_ONLY('Applying dirty-spec fixes');
  },
  // The Formatter (and therefore its document reader) is desktop-only by law
  // — ENGINEERING §9.1. There is no server-side counterpart: the paths this
  // takes are the user's OWN local files, which EC2 cannot see, and an
  // endpoint that read arbitrary server paths would be a file-disclosure hole
  // (§6 least privilege). Web callers get the same DesktopOnly rejection every
  // other formatter file operation gives.
  readFormatterDocx: async () => {
    throw DESKTOP_ONLY('Reading a local Word document');
  },
  openFolder: async () => {},
  openPath: async () => {},

  // ── Format log
  readLog: async () => [],
  clearLog: async () => true,

  // ── Menu events (no native menu in web)
  onMenuNew: () => {},
  onMenuSave: () => {},
  removeMenuListeners: () => {},

  // ── Firm context (server-derived in web)
  switchFirmDb: async () => {},
  closeFirmDb: async () => {},
  setFirmModules: async () => {},

  // ── App close lifecycle (web tab close; guard handled by beforeunload elsewhere)
  onAppCloseRequested: () => {},
  confirmAppClose: async () => {},
  notifyCloseGuardShown: () => {},
  notifyCloseCancelled: () => {},

  // ── Auto-updater (web updates by reload)
  checkForUpdates: async () => ({}),
  downloadUpdate: async () => ({}),
  installUpdate: async () => {},
  getAppVersion: async () => 'web',
  skipUpdateVersion: async () => {},
  remindUpdateLater: async () => {},
  onUpdateAvailable: () => {},
  onUpdateUpToDate: () => {},
  onUpdateDownloadProgress: () => {},
  onUpdateDownloaded: () => {},
  onUpdateError: () => {},

  // ── Zoom
  setZoomFactor: (factor: number) => {
    document.documentElement.style.setProperty('zoom', String(factor));
  },
  getZoomFactor: () => {
    const z = Number(document.documentElement.style.getPropertyValue('zoom'));
    return z > 0 ? z : 1;
  },

  // ── Dropped-file path (no real paths in web; best-effort name)
  getPathForFile: async (file: File) => file.name,
};

// Install only when there is no Electron preload bridge present.
if (typeof window !== 'undefined' && !(window as unknown as { api?: unknown }).api) {
  (window as unknown as { api: SpecFormaApi }).api = webApi;
}

export { webApi };
